import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Lightbulb, MapPin, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const MODES = [["idea", "Business Idea", Lightbulb], ["address", "Address / Site", MapPin], ["franchise", "Franchise", Store]];

export const ModeTabs = ({ mode, setMode, franchise = false }) => (
  <div className="inline-flex flex-wrap rounded-full border border-slate-700 bg-slate-950/60 p-1" data-testid="mode-tabs">
    {MODES.filter(([m]) => franchise || m !== "franchise").map(([m, label, Icon]) => (
      <button key={m} type="button" data-testid={`mode-${m}`} onClick={() => setMode(m)}
        className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm transition-colors ${mode === m ? "bg-emerald-500 text-emerald-950 font-semibold" : "text-slate-400 hover:text-slate-100"}`}>
        <Icon className="h-4 w-4" />{label}
      </button>
    ))}
  </div>
);

export const PromptBox = () => {
  const [mode, setMode] = useState("idea");
  const [prompt, setPrompt] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const generate = async () => {
    if (mode === "address" && !address.trim()) return toast.error("Enter a property address");
    if (!prompt.trim()) return toast.error(mode === "address" ? "Describe what you plan at this address" : "Describe your business idea");
    setLoading(true);
    try {
      const r = await api.post("/studies", { mode, description: prompt, address });
      navigate(`/studies/${r.data.id}`);
    } catch {
      toast.error("Could not start generation");
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-900/90 backdrop-blur-xl p-4 sm:p-5 shadow-2xl shadow-emerald-500/5 text-left" data-testid="prompt-box">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <ModeTabs mode={mode} setMode={setMode} />
        <span className="text-xs text-slate-500">No sign-up needed</span>
      </div>
      {mode === "address" && (
        <div className="relative mb-3">
          <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400" />
          <Input data-testid="prompt-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Property address, e.g. 1100 S Congress Ave, Austin, TX"
            className="pl-9 h-11 bg-slate-950/60 border-slate-700" />
        </div>
      )}
      <textarea data-testid="prompt-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate(); }}
        placeholder={mode === "address" ? "What do you plan here? e.g. a drive-thru coffee shop with 20 parking spaces, budget $400k" : "Feasibility study for a renewable energy startup installing solar rooftops in Lagos, budget $500k"}
        className="w-full resize-none bg-transparent outline-none text-base text-slate-100 placeholder:text-slate-500 px-1" />
      <div className="flex justify-end mt-2">
        <Button data-testid="prompt-generate-btn" disabled={loading} onClick={generate} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold px-6">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate
        </Button>
      </div>
    </div>
  );
};
